import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { PagePaths } from "@/admin/shared/constants";
import {
  useUpdateTranslationWordsMutation,
  type TranslationEditData,
} from "@/admin/entities/translations";
import { EditorHeader } from "./EditorHeader";
import { EditorFilter } from "./EditorFilter";
import { WordsList } from "./WordsList";
import { WordCard } from "./WordCard";
import { CreateWordForm } from "./CreateWordForm";
import { downloadAsCsv } from "./csv";
import { formatKeyConflicts, splitConflictingKeys } from "./keys";
import { WORD_STATUS, type EditorWord, type WordStatus } from "./types";

export interface TranslationEditorProps {
  translation: TranslationEditData;
  languageCode: string;
}

interface RawWord {
  key: string;
  value: string;
}

const UNSAVED_CHANGES_MESSAGE =
  "У вас есть несохраненные изменения. Покинуть страницу без сохранения?";

const FULL_IMPORT_MESSAGE =
  "Импорт CSV заменит весь список ключей и все несохранённые правки текущего языка. Продолжить?";

export function TranslationEditor({
  translation,
  languageCode,
}: TranslationEditorProps) {
  const { variants, project, version, namespace } = translation;

  // вариант перевода на текущем языке
  const variant = useMemo(
    () => variants.find((v) => v.languageCode === languageCode),
    [variants, languageCode]
  );

  // базовые языки для подсказки (label)
  const primaryVariant = useMemo(
    () => variants.find((v) => v.languageCode === "en"),
    [variants]
  );
  const secondaryVariant = useMemo(
    () => variants.find((v) => v.languageCode === "ru"),
    [variants]
  );

  const primaryVariantWordsByKey = useMemo(
    () => primaryVariant?.words.reduce(indexByKey, {}),
    [primaryVariant]
  );
  const secondaryVariantWordsByKey = useMemo(
    () => secondaryVariant?.words.reduce(indexByKey, {}),
    [secondaryVariant]
  );

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [filter, setFilter] = useState<WordStatus | undefined>();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | undefined>();
  const [changed, setChanged] = useState<Record<string, string>>({});
  const [deleted, setDeleted] = useState<Record<string, boolean>>({});
  const [words, setWords] = useState<RawWord[]>(
    () =>
      variant?.words.map((w) => ({ key: w.key, value: w.value ?? "" })) ?? []
  );
  // Baseline «сколько ключей сейчас на сервере». Проп translation после Save
  // не перезапрашивается, поэтому variant.words.length устаревает — считать
  // saveDisabled по нему нельзя (кнопка залипала бы включённой навсегда).
  const [savedWordsCount, setSavedWordsCount] = useState(
    () => variant?.words.length ?? 0
  );

  const [updateTranslationWords, { loading }] =
    useUpdateTranslationWordsMutation();

  // Есть ли несохранённые изменения — считаем по changed/deleted напрямую
  // (а не по производному _words), чтобы хук можно было вызвать безусловно,
  // до раннего return ниже (правила хуков).
  const hasUnsavedChanges =
    Object.keys(changed).length > 0 ||
    Object.keys(deleted).some((key) => deleted[key]);

  // Роутер админки — обычный BrowserRouter (см. admin/app/router.tsx), а не
  // createBrowserRouter/data router, поэтому useBlocker из react-router-dom
  // v7 здесь не сработает (он требует data router). Поэтому предупреждаем о
  // потере изменений тремя реальными путями ухода: закрытие/обновление
  // вкладки (beforeunload), клик по кнопке Close (см. onClickClose ниже) и
  // клик по любой внутренней ссылке вне редактора (сайдбар, логотип) —
  // перехватываем его на capture-фазе document, до onClick роутерного Link.
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // legacy-совместимость: часть браузеров требует непустой returnValue
      event.returnValue = "";
    };

    const handleLinkClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      // модифицированный клик открывает новую вкладку — редактор остаётся
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      // кнопки и пункты меню без href нас не касаются
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      // внешние ссылки (Export JSON, mailto:, другой домен) уводят из
      // приложения — их закрывает beforeunload
      if (url.origin !== window.location.origin) return;
      // ссылка на тот же путь редактор не размонтирует
      if (url.pathname === window.location.pathname) return;

      if (window.confirm(UNSAVED_CHANGES_MESSAGE)) return;

      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleLinkClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [hasUnsavedChanges]);

  // Сортируем отдельно от маппинга: сортировка зависит только от набора
  // ключей, а маппинг пересчитывается на каждое нажатие клавиши (changed).
  const sortedWords = useMemo(() => [...words].sort(sortByKey), [words]);

  const _words = useMemo<EditorWord[]>(
    () =>
      sortedWords.map((rawWord) => {
        let status: WordStatus;
        let value = rawWord.value;

        if (rawWord.key in changed) {
          status = WORD_STATUS.CHANGED;
          value = changed[rawWord.key];
        } else if (rawWord.value) {
          status = WORD_STATUS.TRANSLATED;
        } else {
          status = WORD_STATUS.UNTRANSLATED;
        }

        const label =
          primaryVariantWordsByKey?.[rawWord.key] ||
          secondaryVariantWordsByKey?.[rawWord.key];

        return {
          key: rawWord.key,
          value,
          status,
          label,
          selected: selected === rawWord.key,
          deleted: Boolean(deleted[rawWord.key]),
        };
      }),
    [
      sortedWords,
      changed,
      deleted,
      selected,
      primaryVariantWordsByKey,
      secondaryVariantWordsByKey,
    ]
  );

  const filteredWords = useMemo(() => {
    let result = _words;

    if (filter) {
      result = result.filter((w) => filter === w.status);
    }

    if (query) {
      result = result.filter((w) => someMatch(query, w.key, w.value, w.label));
    }

    return result;
  }, [_words, filter, query]);

  const word = useMemo(
    () => _words.find((w) => w.key === selected),
    [_words, selected]
  );

  const changedCount = Object.keys(changed).length;
  const deletedCount = useMemo(
    () => Object.keys(deleted).filter((key) => deleted[key]).length,
    [deleted]
  );
  const { untranslatedCount, translatedCount } = useMemo(
    () => ({
      untranslatedCount: _words.filter(
        (w) => w.status === WORD_STATUS.UNTRANSLATED
      ).length,
      translatedCount: _words.filter((w) => w.status === WORD_STATUS.TRANSLATED)
        .length,
    }),
    [_words]
  );

  const existingKeys = useMemo(() => new Set(words.map((w) => w.key)), [words]);

  const onClickCreateWord = useCallback(() => {
    setSelected(undefined);
  }, []);

  const onCreateWord = useCallback(
    (newWord: { key: string; value: string }) => {
      setWords((prev) => [newWord, ...prev]);
      setChanged((prev) => ({ ...prev, [newWord.key]: newWord.value }));
      enqueueSnackbar("Ключ создан", { variant: "success" });
    },
    [enqueueSnackbar]
  );

  const onClickWord = useCallback((clicked: EditorWord) => {
    setSelected(clicked.key);
  }, []);

  const onClickDeleteWord = useCallback((clicked: EditorWord) => {
    setDeleted((prev) => ({ ...prev, [clicked.key]: true }));
  }, []);

  const onClickRestoreWord = useCallback((clicked: EditorWord) => {
    setDeleted((prev) => ({ ...prev, [clicked.key]: false }));
  }, []);

  const onClickDeleteAllUntranslated = useCallback(() => {
    const untranslatedWords = _words.filter(
      (w) => w.status === WORD_STATUS.UNTRANSLATED
    );

    setDeleted((prev) =>
      untranslatedWords.reduce((acc, w) => ({ ...acc, [w.key]: true }), prev)
    );
    enqueueSnackbar("Все непереведённые ключи помечены на удаление", {
      variant: "success",
    });
  }, [_words, enqueueSnackbar]);

  const onClickSave = useCallback(async () => {
    // Фикс бага референса: там removeWords = все ключи deleted (включая
    // восстановленные, deleted[key] === false). Мы шлём только реально
    // помеченные на удаление.
    const removeWords = Object.keys(deleted).filter((key) => deleted[key]);
    // Ключ, изменённый, а затем помеченный на удаление, не должен
    // «пережить» Save: без !deleted[w.key] он одновременно попадал бы
    // и в removeWords, и в addWords (removeWords выполняется первым в
    // сервисе, addWords тут же возвращал бы его обратно).
    const addWords = _words
      .filter((w) => w.key in changed && !deleted[w.key])
      .map((w) => ({ key: w.key, value: w.value }));

    try {
      const result = await updateTranslationWords({
        variables: {
          input: {
            translationId: translation.id,
            languageCode,
            removeWords,
            addWords,
          },
        },
      });

      // Ресинкаем words из ответа мутации: без этого удалённые/добавленные
      // ключи продолжают жить только в локальном стейте, saveDisabled
      // навсегда залипает в false, а Save шлёт пустые no-op мутации.
      const savedVariant = result.data?.updateTranslationWords.variants.find(
        (v) => v.languageCode === languageCode
      );

      if (savedVariant) {
        setWords(
          savedVariant.words.map((w) => ({ key: w.key, value: w.value ?? "" }))
        );
        setSavedWordsCount(savedVariant.words.length);
      }

      setChanged({});
      setDeleted({});
      enqueueSnackbar("Перевод сохранён", { variant: "success" });
    } catch (error) {
      enqueueSnackbar((error as Error).message, { variant: "error" });
    }
  }, [
    _words,
    changed,
    deleted,
    enqueueSnackbar,
    languageCode,
    translation.id,
    updateTranslationWords,
  ]);

  const onClickClose = useCallback(() => {
    if (hasUnsavedChanges && !window.confirm(UNSAVED_CHANGES_MESSAGE)) {
      return;
    }

    navigate(PagePaths.translations);
  }, [hasUnsavedChanges, navigate]);

  const onImportError = useCallback(
    (error: Error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    [enqueueSnackbar]
  );

  const onImported = useCallback(
    (importedWords: Record<string, string>) => {
      // Полный импорт заменяет весь список ключей и затирает несохранённые
      // правки — спрашиваем подтверждение.
      if (!window.confirm(FULL_IMPORT_MESSAGE)) return;

      // Ключи вида `a` и `a.b` одновременно сервер не примет — отбрасываем
      // конфликтующие до Save и говорим об этом.
      const { words: safeWords, conflicts } =
        splitConflictingKeys(importedWords);

      const newWords = Object.keys(safeWords).map((key) => ({
        key,
        value: safeWords[key] ?? "",
      }));

      setChanged(safeWords);
      setWords(newWords);
      // Полный импорт задаёт новое состояние целиком: старые пометки на
      // удаление относятся к ключам, которых в списке уже может не быть.
      setDeleted({});
      enqueueSnackbar("Слова импортированы", { variant: "success" });

      if (conflicts.length) {
        enqueueSnackbar(formatKeyConflicts(conflicts), { variant: "warning" });
      }
    },
    [enqueueSnackbar]
  );

  const onPartiallyImported = useCallback(
    (importedWordsByKey: Record<string, string>) => {
      const { words: safeWords, conflicts } = splitConflictingKeys(
        importedWordsByKey,
        existingKeys
      );
      const importedKeys = Object.keys(safeWords);

      if (conflicts.length) {
        enqueueSnackbar(formatKeyConflicts(conflicts), { variant: "warning" });
      }

      if (!importedKeys.length) {
        enqueueSnackbar("В JSON не найдено слов", { variant: "warning" });
        return;
      }

      setChanged((prev) => ({ ...prev, ...safeWords }));

      setDeleted((prev) => {
        let hasChanges = false;
        const next = { ...prev };

        importedKeys.forEach((key) => {
          if (next[key]) {
            next[key] = false;
            hasChanges = true;
          }
        });

        return hasChanges ? next : prev;
      });

      setWords((prev) => {
        const currentKeys = new Set(prev.map(({ key }) => key));
        const updatedWords = prev.map((w) => {
          if (!(w.key in safeWords)) {
            return w;
          }

          return { ...w, value: safeWords[w.key] };
        });
        const newWords = importedKeys
          .filter((key) => !currentKeys.has(key))
          .map((key) => ({ key, value: safeWords[key] }));

        return updatedWords.concat(newWords);
      });

      enqueueSnackbar("Слова частично импортированы", { variant: "success" });
    },
    [enqueueSnackbar, existingKeys]
  );

  const onExportCsv = useCallback(() => {
    const header =
      languageCode === "en" ? ["key", "value"] : ["key", "en value", "value"];
    const rows = _words.map((w) =>
      languageCode === "en" ? [w.key, w.value] : [w.key, w.label ?? "", w.value]
    );
    const name = [project, namespace, version, languageCode].join("/");

    downloadAsCsv([header, ...rows], name);
  }, [_words, languageCode, namespace, project, version]);

  const onChangeWordValue = useCallback(
    (value: string) => {
      if (!word) return;
      setChanged((prev) => ({ ...prev, [word.key]: value }));
    },
    [word]
  );

  if (!variant) {
    return (
      <Box sx={{ p: 2 }}>
        Вариант перевода для языка «{languageCode}» не найден.
      </Box>
    );
  }

  const saveDisabled =
    (changedCount === 0 &&
      deletedCount === 0 &&
      _words.length === savedWordsCount) ||
    loading;

  const exportJsonUrl = `${import.meta.env.VITE_TRANSLATIONS_API_URL || ""}/api/v1/translations/${project}/${version}/${namespace}/${languageCode}`;

  const title = [project, namespace, version, languageCode].join("/");

  return (
    <>
      <EditorHeader
        title={title}
        word={word}
        onClickCreateWord={onClickCreateWord}
        saveDisabled={saveDisabled}
        onClickSave={onClickSave}
        onExportCsv={onExportCsv}
        exportJsonUrl={exportJsonUrl}
        onImported={onImported}
        onImportError={onImportError}
        onPartiallyImported={onPartiallyImported}
        onClose={onClickClose}
      />
      <Box sx={{ p: 1 }}>
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Grid container spacing={1}>
              <Grid size={12}>
                <Card>
                  <EditorFilter
                    filter={filter}
                    onChangeFilter={setFilter}
                    changedCount={changedCount}
                    untranslatedCount={untranslatedCount}
                    translatedCount={translatedCount}
                    query={query}
                    onChangeQuery={setQuery}
                    onClickDeleteAllUntranslated={onClickDeleteAllUntranslated}
                  />
                </Card>
              </Grid>
              <Grid size={12}>
                <Card>
                  <WordsList
                    words={filteredWords}
                    onClickItem={onClickWord}
                    onClickDelete={onClickDeleteWord}
                    onClickRestore={onClickRestoreWord}
                  />
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            {word ? (
              <WordCard
                key={word.key}
                word={word}
                value={word.key in changed ? changed[word.key] : word.value}
                onChange={onChangeWordValue}
              />
            ) : (
              <CreateWordForm
                existingKeys={existingKeys}
                onCreateWord={onCreateWord}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

function sortByKey(a: RawWord, b: RawWord): number {
  if (a.key > b.key) return 1;
  if (a.key < b.key) return -1;
  return 0;
}

function indexByKey(
  all: Record<string, string>,
  item: { key: string; value?: string | null }
): Record<string, string> {
  all[item.key] = item.value ?? "";
  return all;
}

function someMatch(query: string, ...args: (string | undefined)[]): boolean {
  const lowerQuery = query.toLowerCase();
  return args
    .filter((arg): arg is string => Boolean(arg))
    .some((arg) => arg.toLowerCase().includes(lowerQuery));
}
